import React, { useState, useEffect } from 'react';
import { useGame, api, useLiveCoins } from '../context/GameContext';
import { 
  User, Coins, Image,
  Globe, Trophy, Medal
} from 'lucide-react';
import ChessRook from '../components/ChessRook';
import TonWalletCard from '../components/TonWalletCard';

const ProfilePage = () => {
  const { user, t, language, setLanguage, refreshUser, haptic } = useGame();
  const liveCoins = useLiveCoins();
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [collectionValue, setCollectionValue] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadProfileData();
    }
  }, [user?.id]);

  const loadProfileData = async () => {
    setLoading(true);
    try {
      const [leaderRes, rankRes, valueRes] = await Promise.all([
        api.get('/leaderboard?limit=50'),
        api.get(`/leaderboard/me`),
        api.get('/users/me/collection-value').catch(() => ({ data: null })),
      ]);
      setLeaderboard(leaderRes.data.leaderboard);
      setUserRank(rankRes.data);
      setCollectionValue(valueRes.data);
    } catch (err) {
      console.error('Error loading profile data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return <Trophy className="w-5 h-5 text-[#FFD700]" />;
      case 2: return <Medal className="w-5 h-5 text-[#C0C0C0]" />;
      case 3: return <Medal className="w-5 h-5 text-[#CD7F32]" />;
      default: return <span className="w-5 text-center font-mono text-xs text-[#A1A1AA]">{rank}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#A1A1AA]">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-24 px-4">
      {/* Header */}
      <div className="pt-4 pb-4">
        <h1 className="font-heading text-2xl font-light text-[#F5F5F7]">{t('profile')}</h1>
      </div>

      {/* User Card */}
      <div data-testid="user-profile-card" className="glass-card rounded-2xl p-5 mb-4">
        <div className="flex items-center gap-4 mb-5">
          {user?.photo_url ? (
            <img 
              src={user.photo_url} 
              alt={user.username || 'User'} 
              className="w-14 h-14 rounded-full object-cover border-2 border-[#D4AF37]"
            />
          ) : (
            <div className="w-14 h-14 rounded-full bg-[#1a1a2a] flex items-center justify-center border-2 border-[#2a2a3a]">
              <User className="w-7 h-7 text-[#52525B]" />
            </div>
          )}
          <div>
            <h2 className="font-heading text-lg text-[#F5F5F7]">
              @{user?.username || 'Anonymous'}
            </h2>
            <p className="text-xs text-[#A1A1AA]">
              {user?.first_name || ''} {user?.last_name || ''}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <Coins className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="font-mono text-[#F5F5F7]">{(liveCoins ?? (user?.coins || 0)).toLocaleString()}</span>
            </div>
            <p className="text-[10px] text-[#A1A1AA]">{t('coins')}</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <Image className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="font-mono text-[#F5F5F7]">{user?.cards_count || 0}</span>
            </div>
            <p className="text-[10px] text-[#A1A1AA]">{t('cards')}</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <ChessRook className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="font-mono text-[#F5F5F7]">{user?.total_rating || 0}</span>
            </div>
            <p className="text-[10px] text-[#A1A1AA]">{t('rating')}</p>
          </div>
        </div>
        {/* Collection value — actual ⭐ a buyer would pay if they took the
            whole set off your hands today. Sums every painting's badge value
            (auction price OR influence-level × 10). USD uses the fixed
            Telegram Stars rate (~$0.0135 per ⭐). */}
        {collectionValue && collectionValue.total_stars > 0 && (
          <div
            data-testid="collection-value-card"
            className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between"
          >
            <span className="text-[11px] uppercase tracking-wider text-[#A1A1AA]">
              {language === 'ru' ? 'Стоимость коллекции' : 'Collection value'}
            </span>
            <div className="flex items-center gap-1.5">
              <span className="font-mono font-semibold text-[#F5F5F7]">
                {(collectionValue.total_stars || 0).toLocaleString()}
              </span>
              <span className="text-[#D4AF37]">⭐</span>
              <span className="text-[11px] text-[#71717A] font-mono">
                ≈ ${(collectionValue.total_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* TON Wallet */}
      <TonWalletCard />

      {/* Leaderboard */}
      <div data-testid="leaderboard-section" className="glass-card rounded-2xl p-5 mb-4">
        <button
          onClick={() => { haptic(); setShowLeaderboard(!showLeaderboard); }}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="font-medium text-sm text-[#F5F5F7]">{t('leaderboard')}</h3>
          </div>
          <div className="flex items-center gap-2">
            {userRank && (
              <span className="text-xs text-[#A1A1AA]">#{userRank.rank}</span>
            )}
            <div className={`transition-transform ${showLeaderboard ? 'rotate-90' : ''}`}>
              <ChessRook className="w-4 h-4 text-[#D4AF37]" />
            </div>
          </div>
        </button>

        {showLeaderboard && (
          <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5">
            {leaderboard.map((entry) => (
              <div
                key={entry.user_id}
                data-testid={`leaderboard-entry-${entry.rank}`}
                className={`flex items-center gap-2 p-2 rounded-lg ${
                  entry.user_id === user?.id ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30' : 'bg-[#0a0a12]'
                }`}
              >
                <div className="w-5 flex justify-center">{getRankIcon(entry.rank)}</div>
                {entry.photo_url ? (
                  <img src={entry.photo_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-[#1a1a2a] flex items-center justify-center">
                    <User className="w-3.5 h-3.5 text-[#52525B]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-[#F5F5F7] truncate">@{entry.username || 'Anon'}</p>
                </div>
                <div className="flex items-center gap-0.5">
                  <ChessRook className="w-3 h-3 text-[#D4AF37]" />
                  <span className="font-mono text-xs text-[#D4AF37]">{entry.total_rating}</span>
                </div>
              </div>
            ))}
            {leaderboard.length === 0 && (
              <p className="text-center text-xs text-[#A1A1AA] py-3">No players yet</p>
            )}
          </div>
        )}
      </div>

      {/* Language */}
      <div data-testid="language-section" className="glass-card rounded-2xl p-5 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Globe className="w-5 h-5 text-[#D4AF37]" />
          <h3 className="font-medium text-sm text-[#F5F5F7]">{t('language')}</h3>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[
            { code: 'en', label: 'EN' },
            { code: 'ru', label: 'RU' },
            { code: 'es', label: 'ES' },
            { code: 'ar', label: 'AR' },
            { code: 'uk', label: 'UA' },
            { code: 'fr', label: 'FR' },
            { code: 'pt', label: 'PT' },
          ].map((lang) => (
            <button
              key={lang.code}
              data-testid={`lang-${lang.code}`}
              onClick={() => setLanguage(lang.code)}
              className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${
                language === lang.code
                  ? 'bg-[#D4AF37] text-[#05050A]'
                  : 'bg-[#141420] text-[#A1A1AA] hover:text-[#F5F5F7]'
              }`}
            >
              {lang.label}
            </button>
          ))}
        </div>
      </div>

    </div>
  );
};

export default ProfilePage;
